<template>
  <div class="w-full" ref="galleryContainer">
    <!-- Hidden gallery for PhotoSwipe lightbox -->
    <div id="pictureGallery" class="flex gap-2 flex-wrap hidden">
      <a
        v-for="(photo, key) in formatPhoto(photos)"
        :key="key"
        :href="photo.url"
        :data-pswp-download-link="photo.type === 'video' ? photo.videoUrl : photo.url"
        :data-pswp-width="photo.w"
        :data-pswp-height="photo.h"
        :data-pswp-type="photo.type"
        target="_blank"
        rel="noreferrer"
      >
        <img v-if="photo.type === 'video'" :src="photo.videoThumbnailUrl" class="w-full" />
      </a>
    </div>

    <!-- Skeleton placeholders while images preload -->
    <div
      v-if="isLoading && skeletonLayout.boxes.length"
      class="relative w-full min-h-screen"
      :style="{ height: skeletonLayout.containerHeight + 'px' }"
    >
      <div
        v-for="(box, n) in skeletonLayout.boxes"
        :key="'skeleton-' + n"
        class="absolute skeleton-box rounded"
        :style="{
          top: box.top + 'px',
          left: box.left + 'px',
          width: box.width + 'px',
          height: box.height + 'px',
        }"
      />
    </div>

    <!-- Justified row layout -->
    <div
      v-if="justifiedLayout.boxes.length"
      class="relative w-full"
      :style="{ height: justifiedLayout.containerHeight + 'px' }"
    >
      <div
        v-for="(box, index) in justifiedLayout.boxes"
        :key="photos[index]?.url || photos[index]?.previewUrl || index"
        class="absolute overflow-hidden cursor-pointer"
        :class="{
          'scale-[0.96] transition-transform duration-200': longPressHoldingIndex === index,
        }"
        :style="{
          top: box.top + 'px',
          left: box.left + 'px',
          width: box.width + 'px',
          height: box.height + 'px',
        }"
        @click="selectPhotos(photos[index], index)"
        @touchstart.passive="onPhotoPointerDown($event, photos[index], index)"
        @touchmove.passive="onPhotoPointerMove($event)"
        @touchend="onPhotoPointerUp"
        @touchcancel="onPhotoPointerUp"
        @mousedown="onPhotoPointerDown($event, photos[index], index)"
        @mousemove="onPhotoPointerMove($event)"
        @mouseup="onPhotoPointerUp"
      >
        <!-- Selected tick -->
        <img
          v-if="isSelectingPhotos && isPhotoSelected(photos[index])"
          class="absolute z-[1] bottom-2 right-2 w-[20px] md:w-[28px] h-[20px] md:h-[28px] border rounded-full cursor-pointer"
          :class="isGic ? '!border-orange-800' : 'border-[#00A857]'"
          :src="isGic ? '/assets/icons/orange-tick-circle.svg' : '/assets/icons/blue-tick.svg'"
          alt=""
        />
        <!-- Unselected circle -->
        <span
          v-if="isSelectingPhotos && !isPhotoSelected(photos[index])"
          class="absolute z-[1] bottom-2 right-2 block w-[20px] md:w-[28px] h-[20px] md:h-[28px] bg-white border rounded-full cursor-pointer"
          :class="isGic ? 'border-orange-800' : 'border-lightblue'"
        />

        <!-- Image -->
        <img
          v-if="photos[index].type === 'image'"
          :src="photos[index].previewUrl"
          class="w-full h-full object-cover"
        />

        <!-- Video thumbnail + play button -->
        <div v-else class="relative w-full h-full">
          <img :src="photos[index].videoThumbnailUrl" class="w-full h-full object-cover" />
          <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src="../../../public/assets/icons/youtube-player/play-button-dark.svg"
              class="w-10 h-10 lg:w-[48px] lg:h-[48px]"
            />
          </div>
        </div>
      </div>
    </div>

    <PhotoLongPressMenu
      :visible="!!longPressedPhoto"
      :photo="longPressedPhoto"
      :is-downloading="isDownloading"
      :is-gic="isGic"
      @download="onLongPressDownload"
      @select="onLongPressSelect"
      @close="closeLongPressMenu"
    />

    <div class="flex flex-col items-center">
      <BaseButton
        v-if="viewMore"
        class="w-[180px] mt-[12px]"
        data-test-id="gensapp-photodrop-everyone-pixieset"
        :solid-classes="`text-white border-none !leading-[24px] !rounded-[48px] flex justify-center ${isGic ? 'bg-orange hover:bg-[#CC5500]' : 'bg-lightblue hover:bg-[#40BFEB]'}`"
        @click="loadMore"
      >
        <div>{{ $t('copy.photoDropPage.forEveryoneTab.viewMore') }}</div>
      </BaseButton>
    </div>
  </div>
</template>

<script>
import justifiedLayout from 'justified-layout';
import PhotoSwipeLightbox from '../../../node_modules/photoswipe/dist/photoswipe-lightbox.esm';
import PhotoSwipeVideoPlugin from '../../utils/photoswipe-video-plugin.esm.js';
import BaseButton from '../../components/common/BaseButton.vue';
import { mapState } from 'vuex';
import { isIosNativeOnly } from '@/utils/platform-check';
import { StatusBar, Style } from '@capacitor/status-bar';
import PhotoSwipe from '../../../node_modules/photoswipe/dist/photoswipe.esm.js';
import '../../../node_modules/photoswipe/dist/photoswipe.css';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { trackPhotodropIndividualDownload } from '@/utils/photodrop-analytics';
import { isGICPage, isGPCRPage, getConferenceType } from '@/utils/conference-type';
import photoDownloadMixin from '@/mixins/photoDownloadMixin';
import PhotoLongPressMenu from './PhotoLongPressMenu.vue';

const DEFAULT_ASPECT_RATIO = 3 / 2;
const VIDEO_ASPECT_RATIO = 16 / 9;

export default {
  name: 'PhotosEveryone',
  components: {
    BaseButton,
    PhotoLongPressMenu,
  },
  mixins: [photoDownloadMixin],
  data() {
    return {
      lightbox: null,
      options: {
        index: 0,
        pinchToClose: false,
        closeOnScroll: false,
        closeOnVerticalDrag: false,
        preload: [1, 2],
        closeEl: true,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: true,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true,
        spacing: 0.6,
        allowPanToNext: true,
        clickToCloseNonZoomable: false,
        arrowKeys: false,
        getDoubleTapZoom: (isMouseClick, item) => {
          if (isMouseClick) {
            return 1.5;
          } else {
            return item.initialZoomLevel < 0.7 ? 1 : 1.5;
          }
        },
        gallery: '#pictureGallery',
        children: 'a',
        pswpModule: PhotoSwipe,
      },
      photos: [],
      aspectRatios: [],
      containerWidth: 0,
      isLoading: false,
      isLoadingMore: false,
      longPressedPhoto: null,
      longPressTimer: null,
      longPressStartX: 0,
      longPressStartY: 0,
      longPressHoldingIndex: null,
      longPressConsumed: false,
    };
  },
  computed: {
    ...mapState('profile', ['type']),
    ...mapState('photos', {
      photosPreload: 'everyonePhotos',
      viewMore: 'viewMore',
      isSelectingPhotos: 'isSelectingPhotos',
      selectedDayTabGPC: 'selectedDayTabGPC',
      selectedDayTabGPCR: 'selectedDayTabGPCR',
      selectedDayTabGIC: 'selectedDayTabGIC',
      selectedPhotos: 'selectedPhotos',
    }),
    isGic() {
      const _ = this.$route.path; // force recompute on navigation
      return isGICPage();
    },
    selectedDayTab() {
      if (isGPCRPage()) {
        return this.selectedDayTabGPCR;
      } else if (isGICPage()) {
        return this.selectedDayTabGIC;
      } else {
        return this.selectedDayTabGPC;
      }
    },
    skeletonLayout() {
      if (!this.containerWidth || !this.photosPreload.length) {
        return { boxes: [], containerHeight: 0 };
      }
      // Generate varied placeholder aspect ratios for a natural-looking skeleton
      const count = Math.min(this.photosPreload.length, 18);
      const ratios = Array.from({ length: count }, (_, i) => {
        const patterns = [3 / 2, 4 / 3, 1, 2 / 3, 16 / 9, 3 / 4];
        return patterns[i % patterns.length];
      });
      return justifiedLayout(ratios, {
        containerWidth: this.containerWidth,
        targetRowHeight: Math.min(180, Math.floor(this.containerWidth / 3)),
        boxSpacing: 4,
        containerPadding: 0,
        widowLayoutStyle: 'center',
      });
    },
    justifiedLayout() {
      if (!this.containerWidth || !this.photos.length) {
        return { boxes: [], containerHeight: 0 };
      }
      return justifiedLayout(this.aspectRatios, {
        containerWidth: this.containerWidth,
        targetRowHeight: Math.min(180, Math.floor(this.containerWidth / 3)),
        boxSpacing: 4,
        containerPadding: 0,
        widowLayoutStyle: 'center',
      });
    },
  },
  watch: {
    photosPreload: {
      handler() {
        if (this.photosPreload.length === 0) {
          this.photos = [];
          this.aspectRatios = [];
          return;
        }

        this.isLoading = true;
        const newAspectRatios = new Array(this.photosPreload.length).fill(DEFAULT_ASPECT_RATIO);

        const loadImages = this.photosPreload.map((photo, idx) => {
          return new Promise(resolve => {
            if (photo.type === 'image') {
              const img = new Image();
              img.src = photo.previewUrl;
              img.onload = () => {
                if (img.naturalWidth && img.naturalHeight) {
                  newAspectRatios[idx] = img.naturalWidth / img.naturalHeight;
                }
                resolve();
              };
              img.onerror = resolve;
            } else {
              newAspectRatios[idx] = VIDEO_ASPECT_RATIO;
              const thumb = new Image();
              thumb.src = photo.videoThumbnailUrl;
              thumb.onload = () => {
                if (thumb.naturalWidth && thumb.naturalHeight) {
                  newAspectRatios[idx] = thumb.naturalWidth / thumb.naturalHeight;
                }
                resolve();
              };
              thumb.onerror = resolve;
            }
          });
        });

        Promise.all(loadImages).then(() => {
          this.photos = this.photosPreload;
          this.aspectRatios = newAspectRatios;
          this.isLoading = false;
        });
      },
      immediate: true,
    },
  },
  mounted() {
    this._resizeObserver = new ResizeObserver(entries => {
      const width = entries[entries.length - 1]?.contentRect.width;
      if (width === undefined || width === this.containerWidth) return;

      cancelAnimationFrame(this._resizeFrame);
      this._resizeFrame = requestAnimationFrame(() => {
        this._resizeFrame = null;
        if (width !== this.containerWidth) this.containerWidth = width;
      });
    });
    this._resizeObserver.observe(this.$refs.galleryContainer);

    const routePath = this.$route.path;
    const lightbox = new PhotoSwipeLightbox(this.options);
    const _videoPlugin = new PhotoSwipeVideoPlugin(lightbox, {
      autoplay: false,
    });
    lightbox.on('uiRegister', function () {
      const logEvent = function (pathName) {
        const pos = pathName.lastIndexOf('/');
        const fileName = pathName.substring(pos + 1);
        try {
          FirebaseAnalytics.logEvent({
            name: 'click_button',
            params: {
              button_name: 'gensapp-photodrop-photos-everyone-download',
              page: 'photoDrop',
              fileName: fileName,
            },
          });
        } catch (e) {
          // noop
        }
        try {
          window.dataLayer.push({
            event: 'gensapp-photodrop-photos-everyone-download-web',
            file_name: fileName,
          });
        } catch (e) {
          // noop
        }
        trackPhotodropIndividualDownload(fileName, 'single', routePath);
      };
      lightbox.pswp.ui.registerElement({
        name: 'download-button',
        order: 8,
        isButton: true,
        tagName: 'a',
        html: '<svg class="pswp__download__btn" width="32" height="32" viewBox="0 0 32 32" fill="none"><g clip-path="url(#clip0_10929_39313)"><rect width="32" height="32" rx="16" fill="#FFD900"/><g clip-path="url(#clip1_10929_39313)"><path d="M8.99976 20.375V22.125C8.99976 22.5891 9.18413 23.0342 9.51232 23.3624C9.84051 23.6906 10.2856 23.875 10.7498 23.875H21.2498C21.7139 23.875 22.159 23.6906 22.4872 23.3624C22.8154 23.0342 22.9998 22.5891 22.9998 22.125V20.375" stroke="#373E49" stroke-width="1.3125" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.6248 15.125L15.9998 19.5L20.3748 15.125" stroke="#373E49" stroke-width="1.3125" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 9V19.5" stroke="#373E49" stroke-width="1.3125" stroke-linecap="round" stroke-linejoin="round"/></g></g><defs><clipPath id="clip0_10929_39313"><rect width="32" height="32" rx="16" fill="white"/></clipPath><clipPath id="clip1_10929_39313"><rect x="5.5" y="5.5" width="21" height="21" fill="white"/></clipPath></defs></svg>',
        onInit: (el, pswp) => {
          el.setAttribute('download', '');
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener');
          el.setAttribute('data-event-tracking', 'gensapp-photodrop-photos-everyone-download-web');
          pswp.on('change', () => {
            const downloadUrl =
              pswp.currSlide.data.element?.dataset?.pswpDownloadLink || pswp.currSlide.data.src;
            el.removeEventListener('click', logEvent(el.href));
            el.href = downloadUrl;
            el.addEventListener('click', logEvent(el.href));
          });
        },
      });
    });
    lightbox.init();
    lightbox.on('openingAnimationStart', async () => {
      if (isIosNativeOnly()) {
        const pswp = document.getElementsByClassName('pswp__top-bar');
        pswp[0].style.height = '104px';
        pswp[0].style.paddingTop = '44px';
        await StatusBar.setStyle({ style: Style.Dark });
      }
    });
    lightbox.on('closingAnimationStart', async () => {
      if (isIosNativeOnly()) {
        await StatusBar.setStyle({ style: Style.Light });
      }
    });
    this.lightbox = lightbox;
  },
  beforeUnmount() {
    if (this._resizeObserver) this._resizeObserver.disconnect();
    cancelAnimationFrame(this._resizeFrame);
  },
  methods: {
    isPhotoSelected(photo) {
      return this.selectedPhotos.includes(photo.url);
    },
    formatPhoto(photos) {
      return photos.map(photo => ({ ...photo, w: 600, h: 600 }));
    },
    async loadMore() {
      if (this.isLoadingMore) return;
      this.isLoadingMore = true;

      const url = new URL(this.photosPreload[this.photosPreload.length - 1].url);
      await this.$store.dispatch('photos/fetchPhotosEveryone', {
        tabName: this.selectedDayTab,
        startWith: url.pathname.substring(1, url.pathname.length),
        conferenceType: getConferenceType(),
      });

      this.isLoadingMore = false;
    },
    onPhotoPointerDown(event, linkObj, index) {
      if (this.isSelectingPhotos) return;
      const touch = event.touches ? event.touches[0] : event;
      this.longPressStartX = touch.clientX;
      this.longPressStartY = touch.clientY;
      this.longPressHoldingIndex = index;
      this.longPressTimer = setTimeout(() => {
        this.openLongPressMenu(linkObj);
      }, 300);
    },
    onPhotoPointerMove(event) {
      if (this.longPressTimer === null) return;
      const touch = event.touches ? event.touches[0] : event;
      const dx = touch.clientX - this.longPressStartX;
      const dy = touch.clientY - this.longPressStartY;
      if (Math.sqrt(dx * dx + dy * dy) > 10) {
        this.cancelLongPress();
      }
    },
    onPhotoPointerUp() {
      this.cancelLongPress();
    },
    cancelLongPress() {
      if (this.longPressTimer !== null) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      this.longPressHoldingIndex = null;
    },
    openLongPressMenu(linkObj) {
      this.longPressTimer = null;
      this.longPressHoldingIndex = null;
      this.longPressConsumed = true;
      this.longPressedPhoto = linkObj;
    },
    closeLongPressMenu() {
      this.longPressConsumed = false;
      this.longPressedPhoto = null;
    },
    async onLongPressDownload() {
      const photo = this.longPressedPhoto;
      this.closeLongPressMenu();
      const url = photo.type === 'video' ? photo.videoUrl || photo.url : photo.url;
      try {
        const fileName = new URL(url).pathname.split('/').pop();
        trackPhotodropIndividualDownload(fileName, 'long_press_single', this.$route.path);
      } catch (e) {
        // noop
      }
      await this.downloadPhotos([{ url, type: photo.type }]);
    },
    onLongPressSelect() {
      const photo = this.longPressedPhoto;
      this.closeLongPressMenu();
      this.$store.dispatch('photos/updateIsSelectingPhotos', true);
      this.$store.dispatch('photos/addSelectedPhoto', photo.url);
    },
    async selectPhotos(photo, index) {
      if (this.longPressConsumed) {
        this.longPressConsumed = false;
        return;
      }
      if (this.isSelectingPhotos) {
        await this.$store.dispatch('photos/toggleSelectedPhoto', photo.url);
      } else {
        this.lightbox.loadAndOpen(index, {
          gallery: document.querySelector('#pictureGallery'),
        });
      }
    },
  },
};
</script>

<style>
/* Hide scrollbar for Chrome, Safari and Opera */
.day-tabs::-webkit-scrollbar {
  display: none;
}

.skeleton-box {
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
